/**
 * WEB AUTH GUARD SERVICE
 * 
 * Proporciona verificación de seguridad para acceso a datos sensibles en WEB.
 * 
 * NOTA: El 2FA principal se hace al login via SMS.
 * Este servicio es para re-autenticación adicional cuando se accede a
 * datos ultra-sensibles (como notas de sesión específicas).
 * 
 * Estrategia:
 * - Re-autenticación con contraseña para acceder a datos ultra-sensibles
 * - Sesión segura con timeout (5 minutos para datos sensibles)
 * 
 * CUMPLIMIENTO: GDPR Art. 32, HIPAA Security Rule
 */

import { auth } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import { Capacitor } from '@capacitor/core';

// Firebase v8 compat
declare const firebase: any;

const logger = Logger.getLogger('WebAuthGuardService');

// Tiempo de gracia después de verificación (5 minutos)
const SENSITIVE_ACCESS_TIMEOUT_MS = 5 * 60 * 1000;

// Clave para localStorage
const VERIFICATION_STORAGE_KEY = 'appmor_last_verification';

export interface WebAuthGuardStatus {
  isVerified: boolean;
  lastVerifiedAt: number | null;
  requiresReauth: boolean;
}

export interface ReauthResult {
  success: boolean;
  error?: string;
}

class WebAuthGuardService {
  private static instance: WebAuthGuardService;
  
  // Estado de verificación en memoria
  private lastVerification: number = 0;
  
  // Callback para mostrar modal de re-autenticación
  private reauthModalCallback: ((reason: string) => Promise<ReauthResult>) | null = null;
  
  private constructor() {
    if (typeof window !== 'undefined') {
      this.restoreVerificationState();
    }
  }
  
  /**
   * Restaura el estado de verificación desde localStorage
   */
  private restoreVerificationState(): void {
    try {
      const stored = localStorage.getItem(VERIFICATION_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        const userId = auth.currentUser?.uid;
        
        if (data.userId === userId && 
            data.verification && 
            (Date.now() - data.verification) < SENSITIVE_ACCESS_TIMEOUT_MS) {
          this.lastVerification = data.verification;
          logger.info('Restored verification state');
        }
      }
    } catch (e) {
      // Ignorar errores de localStorage
    }
  }
  
  /**
   * Guarda el estado de verificación en localStorage
   */
  private saveVerificationState(): void {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;
      
      localStorage.setItem(VERIFICATION_STORAGE_KEY, JSON.stringify({
        userId,
        verification: this.lastVerification
      }));
    } catch (e) {
      // Ignorar errores de localStorage
    }
  }

  static getInstance(): WebAuthGuardService {
    if (!WebAuthGuardService.instance) {
      WebAuthGuardService.instance = new WebAuthGuardService();
    }
    return WebAuthGuardService.instance;
  }

  /**
   * Verifica si estamos en plataforma web
   */
  isWebPlatform(): boolean {
    return !Capacitor.isNativePlatform();
  }

  /**
   * Registra el callback para mostrar modal de re-autenticación
   */
  setReauthModalCallback(callback: (reason: string) => Promise<ReauthResult>): void {
    this.reauthModalCallback = callback;
    logger.info('Reauth modal callback registered');
  }

  /**
   * Verifica si el usuario tiene acceso a datos sensibles
   */
  async verifySensitiveAccess(reason?: string): Promise<boolean> {
    // En app nativa, usar biometría (no este servicio)
    if (!this.isWebPlatform()) {
      return true;
    }

    // Si hay verificación reciente, permitir
    if (this.hasRecentVerification()) {
      logger.info('Access granted - recent verification');
      return true;
    }

    // Necesita re-autenticación
    logger.info('Sensitive access requires reauthentication', { reason });
    
    if (!this.reauthModalCallback) {
      logger.error('No reauth modal callback registered');
      return false;
    }

    const result = await this.reauthModalCallback(reason || 'Acceso a información sensible');
    
    if (result.success) {
      this.lastVerification = Date.now();
      this.saveVerificationState();
      logger.info('Reauthentication successful');
      return true;
    }

    logger.warn('Reauthentication failed', { error: result.error });
    return false;
  }

  /**
   * Verifica la contraseña del usuario contra Firebase Auth
   */
  async verifyPassword(password: string): Promise<boolean> {
    const user = auth.currentUser;
    if (!user || !user.email) {
      logger.error('No authenticated user found');
      return false;
    }

    try {
      if (typeof firebase === 'undefined' || !firebase.auth?.EmailAuthProvider) {
        logger.error('Firebase SDK not loaded properly');
        return false;
      }
      
      const credential = firebase.auth.EmailAuthProvider.credential(user.email, password);
      await user.reauthenticateWithCredential(credential);
      
      this.lastVerification = Date.now();
      this.saveVerificationState();
      logger.info('Password verification successful');
      return true;
    } catch (error: any) {
      logger.error('Password verification failed', { 
        code: error.code || 'unknown',
        message: error.message || 'Unknown error'
      });
      return false;
    }
  }

  /**
   * Verifica si hay una verificación reciente
   */
  hasRecentVerification(): boolean {
    if (this.lastVerification === 0) return false;
    return (Date.now() - this.lastVerification) < SENSITIVE_ACCESS_TIMEOUT_MS;
  }

  /**
   * Obtiene el estado actual de verificación
   */
  getStatus(): WebAuthGuardStatus {
    return {
      isVerified: this.hasRecentVerification(),
      lastVerifiedAt: this.lastVerification || null,
      requiresReauth: !this.hasRecentVerification()
    };
  }

  /**
   * Tiempo restante antes de requerir re-autenticación (en segundos)
   */
  getTimeRemaining(): number {
    if (this.hasRecentVerification()) {
      const remaining = SENSITIVE_ACCESS_TIMEOUT_MS - (Date.now() - this.lastVerification);
      return Math.max(0, Math.floor(remaining / 1000));
    }
    return 0;
  }

  /**
   * Limpia el estado de sesión segura (logout)
   */
  clearSession(): void {
    this.lastVerification = 0;
    
    try {
      localStorage.removeItem(VERIFICATION_STORAGE_KEY);
    } catch (e) {
      // Ignorar errores
    }
    
    logger.info('Secure session cleared');
  }
}

export const webAuthGuardService = WebAuthGuardService.getInstance();
